import React, { useEffect, useState } from "react";
import axios from "axios";
import PlannerGrid from "./PlannerGrid";
import AnalyticsSection from "./AnalyticsSection";
import AssignShiftModal from "./AssignShiftModal";
import BulkAssignModal from "./BulkAssignModal";
import ShiftSwapModal from "./ShiftSwapModal";
import ShiftListPanel from "./ShiftListPanel";
import PendingRequestsPanel from "./PendingRequestsPanel";
import "./shifts.css";
import "./em-shifts.css";
import { useNavigate } from "react-router-dom";



// CalendarDays / Clock / Hourglass / BarChart3 and Tabs are only used by the
// commented-out tab picker below — restore them together with it.
import { CalendarClock, Plus, LayoutGrid, Download, ArrowLeftRight, /* CalendarDays, Clock, Hourglass, BarChart3, */ Copy, Users } from "lucide-react";
import { PageHeader /*, Tabs */ } from "../../components/em/ui";
import { API_URL } from '../../api/api';
import { hasPermission } from '../../utils/permissionUtils';






import { confirmDialog } from "../../components/em/confirm";
import { toast } from "../../utils/toast";
/* Date Helpers */
const getStartOfWeek = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - day);
  return d;
};

const getStartOfMonth = (date) =>
  new Date(date.getFullYear(), date.getMonth(), 1);

// YYYY-MM from local parts. monthStart is a local midnight, so toISOString()
// rolls back to the previous month for any timezone ahead of UTC — in IST,
// September was requested as "2026-08".
const monthParam = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

// Centralised 401 handler — bounces to login on token expiry.
const handleAuthError = (err) => {
  if (err?.response?.status === 401) {
    localStorage.clear();
    window.location.assign('/login');
    return true;
  }
  return false;
};

const ShiftPlanner = ({ readOnly = false }) => {
  const baseURL = `${API_URL}/api/settings/shifts`;
  const navigate = useNavigate();
  // Assigning, bulk-assigning, copying weeks and creating swaps is
  // `manage_shifts` — the same key the backend enforces on those endpoints —
  // so a custom role granted it can plan shifts too.
  const isReadOnly = readOnly || !hasPermission('manage_shifts');
  const token = localStorage.getItem('token');
  const authHeader = token ? { headers: { Authorization: `Bearer ${token}` } } : {};

  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [copying, setCopying] = useState(false);
  // Tabs: roster | shifts | pending | analytics.
  // Pinned to "roster" while the tab picker below is commented out — it is the
  // only reachable panel, and reading the persisted value would strand anyone
  // who last left the page on Analytics with no control to get back.
  // Restore the localStorage read + persist effect together with the picker.
  const tab = "roster";
  const [view, setView] = useState("week");
  const [weekStart, setWeekStart] = useState(getStartOfWeek(new Date()));
  const [monthStart, setMonthStart] = useState(getStartOfMonth(new Date()));

  const [showAssign, setShowAssign] = useState(false);
  const [showSwap, setShowSwap] = useState(false);
  const [showBulk, setShowBulk] = useState(false);

  const [shifts, setShifts] = useState([]);
  // Whether a read-only viewer gets the Swap Requests button at all: only if
  // their department allows requests or they review someone's. Editors always
  // see it (they can create swaps directly).
  const [swapAccess, setSwapAccess] = useState(false);

  useEffect(() => {
    if (!isReadOnly) return;
    let cancelled = false;
    axios
      .get(`${baseURL}/shift-requests/can-review`, authHeader)
      .then(({ data }) => {
        if (!cancelled) setSwapAccess(!!(data?.canRequest || data?.canReview));
      })
      .catch((err) => {
        handleAuthError(err);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReadOnly]);

  /* Fetch Planner */
  useEffect(() => {
    fetchPlanner();
  }, [weekStart, monthStart, view]);

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const res = await axios.get(`${baseURL}`, authHeader);
      setShifts(res.data);
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error("Failed to load shifts", err);
      setShifts([]);
    }
  };

  const fetchPlanner = async () => {
    try {
      const params =
        view === "week"
          ? { view: "week", start: weekStart.toISOString().split("T")[0] }
          : { view: "month", month: monthParam(monthStart) };

      const res = await axios.get(`${baseURL}/planner`, { ...authHeader, params });

      setEmployees(res.data.employees);
      setAssignments(res.data.assignments);
      setCoverage(res.data.coverage || []);
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error("Error fetching planner:", err);
      setEmployees([]);
      setAssignments([]);
      setCoverage([]);
    }
  };

  // Copy the current week's roster to the next week.
  const handleCopyWeek = async () => {
    const from = weekStart.toISOString().split("T")[0];
    const toDate = new Date(weekStart.getTime() + 7 * 864e5);
    const to = toDate.toISOString().split("T")[0];
    if (!(await confirmDialog(`Copy this week's roster to the week of ${to}? Existing assignments that week will be overwritten.`))) return;
    setCopying(true);
    try {
      const res = await axios.post(`${baseURL}/copy-week`, { from, to }, authHeader);
      toast.error(`Copied ${res.data?.affected || 0} assignment(s) to the week of ${to}.`);
      setWeekStart(getStartOfWeek(toDate));
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err?.response?.data?.error || "Copy week failed");
    } finally {
      setCopying(false);
    }
  };

  // Coverage grouped by date → shifts, with understaffed flag (headcount < min_staff).
  const coverageByDate = React.useMemo(() => {
    const m = new Map();
    for (const c of coverage) {
      const day = String(c.date).slice(0, 10);
      if (!m.has(day)) m.set(day, []);
      m.get(day).push(c);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [coverage]);

  /* Navigation */
  const handlePrev = () => {
    if (view === "week") {
      setWeekStart(
        new Date(weekStart.getTime() - 7 * 24 * 60 * 60 * 1000)
      );
    } else {
      setMonthStart(
        new Date(monthStart.getFullYear(), monthStart.getMonth() - 1, 1)
      );
    }
  };

  const handleNext = () => {
    if (view === "week") {
      setWeekStart(
        new Date(weekStart.getTime() + 7 * 24 * 60 * 60 * 1000)
      );
    } else {
      setMonthStart(
        new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, 1)
      );
    }
  };
const exportToCSV = (filename, employees, assignments) => {
  if (!employees.length) {
    toast.error("No employees to export");
    return;
  }

  // CSV headers
  const headers = ["Employee", "Shift Date", "Shift Type", "Start Time", "End Time"];

  let rows = [];

  if (assignments && assignments.length > 0) {
    rows = assignments.map((assign) => {
      const emp = employees.find((e) => e.id === assign.employee_id);
      const empName = emp ? `${emp.first_name} ${emp.last_name}`.trim() : "Unknown";
      return [
        empName,
        assign.date || "",
        assign.shift_name || "",
        assign.start_time || "",
        assign.end_time || ""
      ];
    });
  } else {
    // Assignments empty → just list employees with empty shift info
    rows = employees.map((emp) => [
      `${emp.first_name} ${emp.last_name}`.trim(),
      "",
      "",
      "",
      ""
    ]);
  }

  // Convert to CSV string
  const csvContent = [
    headers.join(","), 
    ...rows.map((r) => r.map((val) => `"${val}"`).join(",")) 
  ].join("\n");

  // Trigger download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
};


const handleExportCSV = async () => {
  try {
    const params =
      view === "week"
        ? { view: "week", start: weekStart.toISOString().split("T")[0] }
        : { view: "month", month: monthParam(monthStart) };

    const res = await axios.get(`${baseURL}/planner`, { ...authHeader, params });

    const { employees: empData, assignments: assignData } = res.data;

    console.log(assignData);
   
    exportToCSV("shift_planner.csv", empData, assignData);
  } catch (err) {
    console.error(err);
    toast.error("Failed to fetch data for export");
  }
};

  /* Swap Handler — two different endpoints, not one.
     • type "request" → POST /shift-request, open to any authenticated user;
       the server takes the requester from the session.
     • anything else → POST /swap, the manager-only two-employee swap.
     These used to both post to /swap, which only ever accepted
     { employee1, employee2, dates } and rejected a request payload as
     "Employees and dates required". */
  const handleShiftSwap = async (payload) => {
    try {
      if (payload.type === "request") {
        await axios.post(
          `${baseURL}/shift-request`,
          { swap_with: payload.swap_with, dates: payload.dates },
          authHeader,
        );
        toast.success("Swap request submitted!");
      } else {
        await axios.post(`${baseURL}/swap`, payload, authHeader);
        toast.success("Shifts swapped successfully!");
        fetchPlanner();
      }
      setShowSwap(false);
    } catch (err) {
      if (handleAuthError(err)) return;
      console.error(err);
      toast.error(err.response?.data?.error || "Error processing request");
    }
  };

  return (
    <div className="shift-planner-page" style={{ maxWidth: 1440, margin: "0 auto", padding: "0 32px 48px" }}>

      {/* Header */}
      <PageHeader
        icon={CalendarClock}
        title="Shift Planner"
        subtitle="Plan and manage team shifts"
        actions={(
          <div className="btn-wrap shift-btns">
            {!isReadOnly && (
              <button
                className="org-gradi"
                onClick={() => setShowAssign(true)}
              >
                <Plus size={16} /> Assign Shift
              </button>
            )}

            {!isReadOnly && (
              <button className="bulk-btn" onClick={() => setShowBulk(true)}>
                <LayoutGrid size={16} /> Bulk Assign
              </button>
            )}

            <button onClick={handleExportCSV}>
              <Download size={16} /> Export CSV
            </button>

            {!isReadOnly && view === "week" && (
              <button onClick={handleCopyWeek} disabled={copying}>
                <Copy size={16} /> {copying ? "Copying…" : "Copy Week →"}
              </button>
            )}

            {/* A member uses it to raise their own swap request (only if
                their department allows it), a manager to create swaps and
                action the queue. The modal decides which tabs each sees. */}
            {(!isReadOnly || swapAccess) && (
              <button onClick={() => setShowSwap(true)}>
                <ArrowLeftRight size={16} /> Swap Requests
              </button>
            )}
          </div>
        )}
      />

      {/* Tabs — Roster (default) / Shifts / Pending Requests / Analytics.
          Hidden on request. The Shifts / Pending Requests / Analytics panels
          below are left in place and still keyed off `tab`, so restoring the
          picker is just this block plus the commented-out imports and the
          `tab` initialiser above. */}
      {/* <div style={{ marginBottom: 18 }}>
        <Tabs
          tabs={[
            { key: "roster", label: "Roster", icon: CalendarDays },
            { key: "shifts", label: "Shifts", icon: Clock },
            { key: "pending", label: "Pending Requests", icon: Hourglass },
            { key: "analytics", label: "Analytics", icon: BarChart3 },
          ]}
          active={tab}
          onChange={setTab}
        />
      </div> */}

      {tab === "roster" && (
        <PlannerGrid
          employees={employees}
          assignments={assignments}
          weekStart={weekStart}
          monthStart={monthStart}
          view={view}
          setView={setView}
          onPrev={handlePrev}
          onNext={handleNext}
        />
      )}

      {/* Coverage — headcount by shift per day; understaffed days flagged. */}
      {tab === "roster" && coverageByDate.length > 0 && (
        <div style={{ marginTop: 18, background: "var(--em-bg-card)", border: "1px solid var(--em-border)", borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 2px rgba(0,0,0,0.04)" }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
            <Users size={15} /> Coverage
            <span style={{ fontWeight: 400, fontSize: 12, color: "#94a3b8" }}>· assigned vs minimum per shift</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {coverageByDate.map(([day, shifts]) => {
              const short = new Date(`${day}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
              return (
                <div key={day} style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ minWidth: 110, fontSize: 12.5, fontWeight: 600, color: "#64748b" }}>{short}</span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {shifts.map((c) => {
                      const under = Number(c.min_staff) > 0 && Number(c.headcount) < Number(c.min_staff);
                      return (
                        <span key={c.shift_id} title={under ? `Understaffed: ${c.headcount}/${c.min_staff}` : `${c.headcount} assigned`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px", borderRadius: 9999,
                            fontSize: 12, fontWeight: 600,
                            background: under ? "rgba(231,0,11,0.10)" : "var(--em-bg-muted, #f1f5f9)",
                            color: under ? "#e7000b" : "#334155",
                            border: under ? "1px solid rgba(231,0,11,0.35)" : "1px solid transparent",
                          }}>
                          <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color || "#94a3b8" }} />
                          {c.shift_name}: {c.headcount}{Number(c.min_staff) > 0 ? `/${c.min_staff}` : ""}
                          {under && " ⚠"}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "shifts" && (
        <ShiftListPanel
          readOnly={isReadOnly}
          onChanged={() => {
            fetchShifts();
            fetchPlanner();
          }}
        />
      )}

      {tab === "pending" && (
        <PendingRequestsPanel
          readOnly={isReadOnly}
          canDecide={!isReadOnly}
        />
      )}

      {tab === "analytics" && <AnalyticsSection />}

      {/* Modals — write-only, hidden in read-only mode. */}
      {!isReadOnly && showAssign && (
        <AssignShiftModal
          onClose={() => setShowAssign(false)}
          onSuccess={fetchPlanner}
        />
      )}
      {!isReadOnly && showBulk && (
        <BulkAssignModal
          shifts={shifts}
          onClose={() => setShowBulk(false)}
          onSuccess={fetchPlanner}
        />
      )}
      {showSwap && (
        <ShiftSwapModal
          employees={employees}
          shifts={shifts}
          isReadOnly={isReadOnly}
          onClose={() => setShowSwap(false)}
          onSwap={handleShiftSwap}
        />
      )}

    </div>
  );
};

export default ShiftPlanner;
